import { queryOptions } from '@tanstack/react-query';
import { SearchParamsOption } from 'ky';
import { samplingApi } from '../apis/sampling';
import { OrdersValuesFilters } from '../types/orders';
import { SamplingPlansValuesFilters } from '../types/sampling';

import {
  Filters,
  getQueriesWithGlobal,
  Globals,
  useQueriesWithGlobal,
} from '@data-access';

export const samplingQueries = (global: Globals) => ({
  all: () => [global, 'sampling'],

  plans: {
    all: () => [...samplingQueries(global).all(), 'plans'],
    lists: () => [...samplingQueries(global).plans.all(), 'lists'],
    list: (filters: Filters) =>
      queryOptions({
        queryKey: [...samplingQueries(global).plans.lists(), filters],
        queryFn: () => samplingApi.plans.list(filters),
      }),
    details: () => [...samplingQueries(global).all(), 'detail'],
    detail: (id: number) =>
      queryOptions({
        queryKey: [...samplingQueries(global).plans.details(), id],
        queryFn: () => samplingApi.plans.getById(id),
      }),
    values: (filters: SamplingPlansValuesFilters) =>
      queryOptions({
        queryKey: [...samplingQueries(global).plans.all(), 'values', filters],
        queryFn: () => samplingApi.plans.getValues(filters),
      }),
    maps: (filters: Filters) =>
      queryOptions({
        queryKey: [...samplingQueries(global).plans.all(), 'maps', filters],
        queryFn: () => samplingApi.plans.getMaps(filters),
      }),
    carbon: {
      all: () => [...samplingQueries(global).plans.all(), 'carbon'],
      debug: (id: number) =>
        queryOptions({
          queryKey: [
            ...samplingQueries(global).plans.carbon.all(),
            'debug',
            id,
          ],
          queryFn: () => samplingApi.plans.carbon.getDebugDetails(id),
        }),
    },
    density: {
      all: () => [...samplingQueries(global).plans.all(), 'density'],
      debug: (id: number) =>
        queryOptions({
          queryKey: [
            ...samplingQueries(global).plans.density.all(),
            'debug',
            id,
          ],
          queryFn: () => samplingApi.plans.density.getDebugDetails(id),
        }),
    },
  },

  orders: {
    all: () => [...samplingQueries(global).all(), 'orders'],
    lists: () => [...samplingQueries(global).orders.all(), 'lists'],
    list: (filters: Filters) =>
      queryOptions({
        queryKey: [...samplingQueries(global).orders.lists(), filters],
        queryFn: () => samplingApi.orders.list(filters),
      }),
    details: () => [...samplingQueries(global).all(), 'detail'],
    detail: (id: number) =>
      queryOptions({
        queryKey: [...samplingQueries(global).orders.details(), id],
        queryFn: () => samplingApi.orders.getById(id),
      }),
    values: (filters: OrdersValuesFilters) =>
      queryOptions({
        queryKey: [...samplingQueries(global).orders.all(), 'values', filters],
        queryFn: () => samplingApi.orders.getValues(filters),
      }),
    filters: () =>
      queryOptions({
        queryKey: [...samplingQueries(global).orders.all(), 'filters'],
        queryFn: () => samplingApi.orders.getFilters(),
      }),
  },

  pratiques: {
    all: () => [...samplingQueries(global).all(), 'pratiques'],
    lists: () => [...samplingQueries(global).pratiques.all(), 'lists'],
    list: (filters: Filters) =>
      queryOptions({
        queryKey: [...samplingQueries(global).pratiques.lists(), filters],
        queryFn: () => samplingApi.pratiques.list(filters),
      }),
  },
});

export const useSamplingQueries = () => {
  const queries = useQueriesWithGlobal(samplingQueries);
  return queries;
};

export const getSamplingQueries = () => getQueriesWithGlobal(samplingQueries);
